import { getColor } from '@/shared';
import { SortByType } from '@/shared/helpers/sort';
import { ColorContext } from '@/shared/providers/ColorContext';
import { useBreakpoints } from '@/shared/providers/Device';
import { useMicrocopy } from '@/shared/providers/Microcopy/MicrocopyHook';
import { useThemeSection } from '@/shared/providers/SectionContext';
import { MindStoryblok } from '@/shared/types/storyblok/storyblok-types';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useState } from 'react';
import Container from '../../Container/Container';
import styles from '../MindListing.module.scss';
import { replaceNumber, sortContent } from '../utils';
import MindCard from './MindCard/MindCard';
import SelectContent from './SelectedContent/SelectedContent';

type MindListingCoreProps = {
    content: MindStoryblok[];
    sortBy: SortByType;
    loadMoreButton?: string;
    startingItems: number;
    uniqueId?: string;
};

const MindListingCore: React.FC<MindListingCoreProps> = ({
    content: originalContent,
    sortBy,
    loadMoreButton,
    startingItems,
    uniqueId,
}) => {
    const theme = useThemeSection();
    const isDark = theme === 'Dark';
    const hexColor = useContext(ColorContext);
    const router = useRouter();
    const { microcopy } = useMicrocopy();

    const [shownCount, setShownCount] = useState(startingItems);
    const [selectedMind, setSelectedMind] = useState<any>(undefined);
    const [content, setContent] = useState<MindStoryblok[]>([]);

    const { isXXSmall, isExtraSmall, isSmall } = useBreakpoints();
    const isMobile = isXXSmall || isExtraSmall || isSmall;

    const remainingItems = useMemo(
        () => Math.min(startingItems, content?.length - shownCount),
        [content, shownCount, startingItems],
    );

    const handleMenuItemClick = (id: string) => {
        if (id === selectedMind) {
            setSelectedMind(undefined);
        } else {
            setSelectedMind(id);
        }
        setTimeout(() => {
            const anchor = document.getElementById(id);

            if (anchor) {
                const offset = isMobile ? 16 : 160;
                const y = anchor.getBoundingClientRect().top + window.scrollY - offset;

                if (id === selectedMind) {
                    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }
        }, 50);
    };

    const addMoreItems = () => {
        setShownCount((prevState: number) => prevState + startingItems);
        setContent(sortContent(content, sortBy).slice(0, shownCount + startingItems));
    };

    useEffect(() => {
        const activeContent = originalContent.filter(item => item.content.active === true);
        setContent(sortContent(activeContent, sortBy).slice(0, startingItems));
    }, [originalContent, sortBy, startingItems]);

    useEffect(() => {
        setSelectedMind(undefined);
    }, [router.locale]);

    const MindsCard = ({ array }: { array: MindStoryblok[] }) => {
        return (
            <>
                {array.map((element: MindStoryblok) => {
                    return (
                        <MindCard
                            key={element.id}
                            element={element}
                            uniqueId={uniqueId}
                            isDark={isDark}
                            isActive={selectedMind === `${uniqueId + element.id}`}
                            onClick={() => handleMenuItemClick(`${uniqueId + element.id}`)}
                        >
                            {selectedMind === `${uniqueId + element.id}` && (
                                <SelectContent
                                    setSelectedContent={() => handleMenuItemClick(`${uniqueId + element.id}`)}
                                    selectedContent={element.content}
                                    isDark={isDark}
                                    featuredArticleButton={microcopy.featuredArticleButton}
                                />
                            )}
                        </MindCard>
                    );
                })}
            </>
        );
    };

    return (
        <>
            <Container code="custom" className={{ wrap: clsx(styles.root, { [styles.dark]: isDark }) }}>
                <div className={styles.row}>{content && <MindsCard array={content} />}</div>
                {content && content.length > content?.length && (
                    <div
                        className={styles.button}
                        style={{ borderColor: getColor(hexColor, isDark) }}
                        onClick={() => {
                            addMoreItems();
                        }}
                    >
                        {replaceNumber(loadMoreButton || 'Load {number} more', remainingItems)}
                    </div>
                )}
            </Container>
        </>
    );
};

export default MindListingCore;
